import { datadogLogs } from '@datadog/browser-logs';
import { useMutation } from '@tanstack/react-query';
import { getExclusionChannelsList } from '../../../api/config/growth/v0';
import { postApiFn } from '../../../api/helper';
import { ProxyChannelsListResponse } from '../../../types/proxyApi';

export const QUERY_KEY = 'sample-exclusion-channels';

interface RequestParams {
  bsp_concepts?: string[];
  countries?: string[];
  limit?: number;
  page?: number;
  maximum_risk_level?: number;
  sort_field?: string;
  sort_dir?: 'asc' | 'desc';
}

type Response = ProxyChannelsListResponse;

const useExclusionChannelsListMutation = ({
  page = 1,
  limit = 1000, // 10k is server default if no value provided
  bsp_concepts = [],
  countries = [],
  // Extracting to safely ignore — these are not forwarded to the request body
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  sort_field,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  sort_dir,
  maximum_risk_level = 3,
  ...params
}: RequestParams) => {
  const path = getExclusionChannelsList();
  const safePage = Math.max(0, page);
  const offset = limit * safePage;
  return useMutation({
    mutationKey: [
      QUERY_KEY,
      `limit=${limit}`,
      `offset=${offset}`,
      `bsp_concepts=${bsp_concepts.join('')}`,
      `countries=${countries}`,
      `risk=${maximum_risk_level}`,
    ],
    mutationFn: async () => {
      return await postApiFn(path, {
        ...params,
        bsp_concepts,
        countries,
        limit,
        offset,
        maximum_risk_level: Math.min(Math.max(0, maximum_risk_level), 3),
      })
        .then((res: Response) => {
          datadogLogs.logger.info('Retrieved results for the sample exclusion channels list', {
            res,
          });
          return res;
        })
        .catch((error) => {
          datadogLogs.logger.error(
            'Failed to retrieve results for the sample exclusion channels list',
            {
              error,
            },
          );
          throw new Error(error);
        });
    },
  });
};

export default useExclusionChannelsListMutation;
